import { take, put, call, all, select, fork } from 'redux-saga/effects';
import set from 'lodash.set';
import get from 'lodash.get';
import uuid from 'uuid/v4';

import { strippedKnownQuery } from 'yeself-common/ModelUtils';
import { LAYER_TYPE } from 'yeself-common/typings/guide/LayerEnum';

import { respawn } from 'yeself-client-common/utils/SagaUtils';
import log from 'yeself-client-common/utils/LogUtils';
import {
    appendDoNotStartAutomaticallyRule,
    filterDoNotStartAutomaticallyRule,
} from 'yeself-client-common/rules/Rules';
import EnvironmentConfiguration from 'yeself-client-common/EnvironmentConfiguration';

import {
    selectAppId,
    selectCompanySystemId,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import { pushNotificationToStack } from 'Console/data/notification/actions';
import { selectConsoleLanguage, selectContentLanguageCode } from 'Console/data/app/selectors';

import {
    FETCH_APPLICATION_GUIDES,
    REMOVE_GUIDE,
    DUPLICATE_GUIDE,
    UPDATE_GUIDE,
    UPDATE_GUIDE_SUCCESS,
    EDIT_GUIDE_HOST,
    CREATE_GUIDE,
    UPDATE_TOUR,
    FETCH_GUIDE_RULES,
    UPDATE_GUIDE_RULES,
    UPDATE_GUIDE_RULES_SUCCESS,
    FETCH_ATTRIBUTES_FOR_RULE_REQUEST,
    FETCH_CONTEXT_OPTIONS_FOR_RULE_REQUEST,
} from './constants';
import {
    fetchApplicationGuidesSuccess,
    fetchApplicationGuidesError,
    removeGuideError,
    removeGuideSuccess,
    duplicateGuideSuccess,
    duplicateGuideError,
    updateGuideSuccess,
    updateGuideError,
    editGuideHostSuccess,
    editGuideHostError,
    createNewGuideSuccess,
    createNewGuideError,
    fetchGuideRulesError,
    updateTourSuccess,
    updateTourError,
    fetchGuideRulesSuccess,
    updateGuideRules,
    updateGuideRulesSuccess,
    updateGuideRulesError,
    fetchAttributesForRuleSuccess,
    fetchAttributesForRuleError,
    fetchContextOptionsForRuleSuccess,
    fetchContextOptionsForRuleError,
} from './actions';
import {
    fetchBatchedApplicationGuides,
    fetchApplicationGuides,
    removeApplicationGuide,
    duplicateGuide,
    updateGuide,
    updateGuideHost,
    createNewGuideRequest,
    updateTour,
    fetchGuideRulesService,
    updateGuideRulesService,
    fetchAttributeForRuleService,
    fetchContextOptionsForRuleService,
} from './services';

const watchUpdateGuideRulesSuccess = (id) => ({ type, payload }) =>
    type === UPDATE_GUIDE_RULES_SUCCESS && payload.guideId === id;

const watchUpdateGuideSuccess = (id) => ({ type, payload }) =>
    type === UPDATE_GUIDE_SUCCESS && payload.guide.id === id;

function* fetchApplicationGuidesWatcher() {
    while (true) {
        const {
            payload: { appId },
        } = yield take(FETCH_APPLICATION_GUIDES);

        try {
            const enableFetchGuideBatching = EnvironmentConfiguration.ENABLE_GUIDE_FETCH_BATCHING;
            const resp = enableFetchGuideBatching
                ? yield call(fetchBatchedApplicationGuides, appId)
                : yield call(fetchApplicationGuides, appId);

            const locale = yield select(selectConsoleLanguage);
            const guides = enableFetchGuideBatching ? resp.guides : resp.body.data.guides;

            const sortedData = guides.sort((a, b) => {
                const firstLabel = a.name[locale.code] || a.name.en;
                const secondLabel = b.name[locale.code] || b.name.en;
                if (firstLabel && secondLabel) {
                    return firstLabel.localeCompare(secondLabel);
                }
                return firstLabel;
            });

            yield put(fetchApplicationGuidesSuccess(sortedData));
        } catch (err) {
            log.error(err);
            yield put(fetchApplicationGuidesError());
        }
    }
}

function* deleteApplicationGuideWatcher() {
    while (true) {
        const { payload } = yield take(REMOVE_GUIDE);
        const applicationId = yield select(selectAppId);

        try {
            yield call(removeApplicationGuide, applicationId, payload);
            yield put(removeGuideSuccess(payload));
        } catch (err) {
            log.error(err);
            yield put(removeGuideError(payload));
        }
    }
}

function* duplicateGuideWatcher() {
    while (true) {
        const { payload } = yield take(DUPLICATE_GUIDE);
        const applicationId = yield select(selectAppId);
        const contentLanguageCode = yield select(selectContentLanguageCode);
        const guideLanguage = payload.name[contentLanguageCode]
            ? contentLanguageCode
            : Object.keys(payload.name)[0];

        const guideNameOverride = { name: { [guideLanguage]: `${payload.name[guideLanguage]} - Copy` } };

        try {
            const resp = yield call(duplicateGuide, applicationId, payload.id, guideNameOverride);
            yield put(duplicateGuideSuccess(resp.body.data, payload.id));
        } catch (err) {
            log.error(err);
            yield put(duplicateGuideError(payload));
        }
    }
}

function* updateGuideTask(payload) {
    const { guide, data, message, secondMessage } = payload;
    const applicationId = yield select(selectAppId);
    Object.keys(data).forEach((key) => {
        if (data[key] === undefined) {
            delete data[key];
        }
    });

    const prefixInitialUrl = (url = '') => {
        const pattern = new RegExp('^(http|https)://');

        if (pattern.test(url)) {
            return url;
        }
        return `https://${url}`;
    };

    const updatedData = {
        ...data,
        initialUrl:
            data.initialUrl && data.initialUrl.length > 0
                ? prefixInitialUrl(strippedKnownQuery(data.initialUrl))
                : '',
    };

    const updatedGuide = {
        description: updatedData.description,
        id: guide.id,
        initialUrl:
            updatedData.initialUrl && updatedData.initialUrl.length > 0
                ? prefixInitialUrl(strippedKnownQuery(updatedData.initialUrl))
                : '',
        name: updatedData.name,
    };

    /**
     * @todo this is badly design update rule and update guide should be splitted entirely
     */
    if (guide.type !== LAYER_TYPE.RECORDING) {
        yield put(updateGuideRules(guide, updatedData, secondMessage));
    }

    try {
        yield call(updateGuide, applicationId, guide.id, updatedGuide);
        yield put(updateGuideSuccess(guide, updatedData));

        const watcher = yield take(watchUpdateGuideRulesSuccess(guide.id));

        if (watcher) {
            yield put(pushNotificationToStack(message.success));
        } else {
            yield put(pushNotificationToStack(message.fail));
        }
    } catch (err) {
        log.error(err);
        yield put(pushNotificationToStack(message.fail));
        yield put(updateGuideError());
    }
}

function* updateGuideWatcher() {
    while (true) {
        const { payload } = yield take(UPDATE_GUIDE);
        yield fork(updateGuideTask, payload);
    }
}

function* updateTourTask(payload, message) {
    const { values, tour } = payload;
    try {
        const applicationId = yield select(selectAppId);
        const guideResp = yield call(fetchGuideRulesService, applicationId, tour.id);
        const { editorSettings, graph } = guideResp.body.data;

        const updatedGraph = { ...graph };
        Object.keys(values).forEach((item) => {
            set(updatedGraph, item, values[item]);
        });

        const tourData = {
            editorSettings,
            graph: updatedGraph,
        };

        yield call(updateTour, applicationId, tour.id, tourData);
        yield put(updateTourSuccess(tour.id, tourData));
        yield put(pushNotificationToStack(message.success));
    } catch (err) {
        log.error(err);
        yield put(pushNotificationToStack(message.fail));
        yield put(updateTourError(payload));
    }
}

function* updateTourWatcher() {
    while (true) {
        const { payload, message } = yield take(UPDATE_TOUR);
        yield fork(updateTourTask, payload, message);
    }
}

function* updateGuideRulesTask(payload, message) {
    const { guide, data } = payload;

    const updatedGuide = {
        editorSettings: { ...guide.editorSettings },
        graph: { ...guide.graph },
    };

    const invertedSandboxValue = !data.sandbox;

    const sandbox =
        data.sandbox === undefined ? get(guide, 'graph.metadata.sandbox') : invertedSandboxValue;

    const healthCheck =
        data.healthCheck === undefined ? get(guide, 'graph.metadata.healthCheck') : data.healthCheck;

    const contextHelp =
        data.contextHelp === undefined ? get(guide, 'graph.metadata.contextHelp') : data.contextHelp;

    const startAutomatically = data.startAutomatically === undefined ? true : data.startAutomatically;

    const { identity, visitor } = data;

    const rules = startAutomatically
        ? filterDoNotStartAutomaticallyRule({ identity, visitor })
        : appendDoNotStartAutomaticallyRule({ identity, visitor });

    set(updatedGuide, 'graph.metadata.rulesDefinition', rules);
    set(updatedGuide, 'graph.metadata.sandbox', sandbox);
    set(updatedGuide, 'graph.metadata.healthCheck', healthCheck);
    set(updatedGuide, 'graph.metadata.contextHelp', contextHelp);

    try {
        const applicationId = yield select(selectAppId);
        const watcher = yield take(watchUpdateGuideSuccess(guide.id));

        yield call(updateGuideRulesService, applicationId, guide.id, updatedGuide);
        yield put(updateGuideRulesSuccess(updatedGuide, guide.id));

        if (watcher) {
            yield put(pushNotificationToStack(message.success));
        } else {
            yield put(pushNotificationToStack(message.fail));
        }
    } catch (err) {
        log.error(err);
        yield put(pushNotificationToStack(message.fail));
        yield put(updateGuideRulesError());
    }
}

function* updateGuideRulesWatcher() {
    while (true) {
        const { payload, message } = yield take(UPDATE_GUIDE_RULES);
        yield fork(updateGuideRulesTask, payload, message);
    }
}

function* editGuideHostWatcher() {
    while (true) {
        const {
            payload: { guideId, message, data },
        } = yield take(EDIT_GUIDE_HOST);
        const applicationId = yield select(selectAppId);

        // If it's publish to same application do not
        // remove guide from guide list
        let isSameApplication = false;
        if (applicationId === data.application.id) {
            isSameApplication = true;
        }

        const prefixInitialUrl = (url = '') => {
            const pattern = new RegExp('^(http|https)://');

            if (pattern.test(url)) {
                return url;
            }
            return `https://${url}`;
        };

        const updatedData = {
            applicationId: data.application.id,
            host: data.host,
            newHost: prefixInitialUrl(strippedKnownQuery(data.newHost)),
            id: guideId,
        };

        try {
            const { body } = yield call(updateGuideHost, applicationId, guideId, updatedData);
            yield put(editGuideHostSuccess(guideId, body, isSameApplication));
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(pushNotificationToStack(message.fail));
            yield put(editGuideHostError(data));
        }
    }
}

function* createNewGuideWatcher() {
    while (true) {
        const { payload, message } = yield take(CREATE_GUIDE);
        const applicationId = yield select(selectAppId);
        const contentLanguageCode = yield select(selectContentLanguageCode);

        const prefixInitialUrl = (url = '') => {
            const pattern = new RegExp('^(http|https)://');

            if (pattern.test(url)) {
                return url;
            }
            return `https://${url}`;
        };

        try {
            const newGuideData = {
                id: uuid(),
                description: { [contentLanguageCode]: payload.description },
                name: { [contentLanguageCode]: payload.name },
                initialUrl: payload.initialUrl
                    ? prefixInitialUrl(strippedKnownQuery(payload.initialUrl))
                    : '',
                type: payload.type,
                subType: payload.subType,
            };

            const resp = yield call(createNewGuideRequest, applicationId, newGuideData);
            const newGuide = {
                ...resp.body,
                isNew: true,
            };
            yield put(createNewGuideSuccess(newGuide));
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(createNewGuideError());
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* fetchGuideRulesWatcher() {
    while (true) {
        const {
            payload: { guideId },
            message,
        } = yield take(FETCH_GUIDE_RULES);

        try {
            const applicationId = yield select(selectAppId);
            const resp = yield call(fetchGuideRulesService, applicationId, guideId);

            yield put(fetchGuideRulesSuccess({ payload: resp.body.data, guideId }));
        } catch (err) {
            log.error(err);
            yield put(fetchGuideRulesError());
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* fetchAttributeForRulesTask() {
    try {
        const applicationId = yield select(selectAppId);
        const resp = yield call(fetchAttributeForRuleService, applicationId);
        yield put(fetchAttributesForRuleSuccess(resp.body.data.attribute_usage));
    } catch (err) {
        log.error(err);
        yield put(fetchAttributesForRuleError());
    }
}

function* fetchAttributeForRulesWatcher() {
    while (true) {
        yield take(FETCH_ATTRIBUTES_FOR_RULE_REQUEST);
        yield fork(fetchAttributeForRulesTask);
    }
}

function* fetchContextOptionsForRuleTask() {
    try {
        const companyId = yield select(selectCompanySystemId);
        const response = yield call(fetchContextOptionsForRuleService, companyId);

        yield put(fetchContextOptionsForRuleSuccess(response.body));
    } catch (err) {
        log.error(err);
        yield put(fetchContextOptionsForRuleError());
    }
}

function* fetchContextOptionsForRuleWatcher() {
    while (true) {
        const { payload } = yield take(FETCH_CONTEXT_OPTIONS_FOR_RULE_REQUEST);
        yield fork(fetchContextOptionsForRuleTask, payload);
    }
}

export default function* guidesSaga() {
    yield respawn(fetchApplicationGuidesWatcher);
    yield respawn(deleteApplicationGuideWatcher);
    yield respawn(duplicateGuideWatcher);
    yield respawn(updateGuideWatcher);
    yield respawn(updateTourWatcher);
    yield respawn(editGuideHostWatcher);
    yield respawn(createNewGuideWatcher);
    yield respawn(fetchGuideRulesWatcher);
    yield respawn(updateGuideRulesWatcher);
    yield respawn(fetchAttributeForRulesWatcher);
    yield respawn(fetchContextOptionsForRuleWatcher);
}
